import { Link, useParams } from "react-router-dom"
import { useQuery } from "@tanstack/react-query"
import { apiTenant } from "../lib/api"

interface MeResponse {
  employee: {
    fullName: string
    employeeNo: string
    joinedAt: string
    status: string
  }
}

interface Payslip {
  id: string
  netPay: string
  run: { periodStart: string; periodEnd: string; status: string }
}

interface LeaveBalance {
  leaveType: { code: string; name: string; paid: boolean }
  entitledDays: string
  usedDays: string
  pendingDays: string
}

interface PendingRequest {
  id: string
  status: string
  fromDate: string
  toDate: string
  days: string
  leaveType: { code: string }
}

function fmtMoney(n: string | number): string {
  return Number(n).toLocaleString("en-LK", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function MyDashboard() {
  const { slug = "" } = useParams<{ slug: string }>()

  const me = useQuery({
    queryKey: ["my-me", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<MeResponse>(slug, "/me"),
  })
  const payslips = useQuery({
    queryKey: ["my-payslips", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ payslips: Payslip[] }>(slug, "/me/payslips"),
  })
  const balances = useQuery({
    queryKey: ["my-balances", slug],
    enabled: !!slug,
    queryFn: () =>
      apiTenant<{ balances: LeaveBalance[] }>(slug, `/me/leave/balances?year=${new Date().getFullYear()}`),
  })
  const pendingLeave = useQuery({
    queryKey: ["my-leave-pending", slug],
    enabled: !!slug,
    queryFn: () => apiTenant<{ requests: PendingRequest[] }>(slug, "/me/leave/requests"),
  })

  const recent = (payslips.data?.payslips ?? []).slice(0, 3)
  const pendingCount = (pendingLeave.data?.requests ?? []).filter((r) => r.status === "pending").length

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">
          {me.data ? `Welcome, ${me.data.employee.fullName.split(" ")[0]}` : "My dashboard"}
        </h1>
        {me.data && (
          <p className="text-xs text-slate-500 mt-1">
            <span className="font-mono">{me.data.employee.employeeNo}</span> · joined{" "}
            {me.data.employee.joinedAt.slice(0, 10)} · status {me.data.employee.status}
          </p>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat
          label="Latest payslip"
          value={recent[0] ? `LKR ${fmtMoney(recent[0].netPay)}` : "—"}
          sub={recent[0] ? `${recent[0].run.periodStart.slice(0, 10)} → ${recent[0].run.periodEnd.slice(0, 10)}` : "no payslips yet"}
          accent
        />
        <Stat label="Total payslips" value={String(payslips.data?.payslips.length ?? 0)} sub="lifetime" />
        <Stat
          label="Pending leave"
          value={String(pendingCount)}
          sub={pendingCount > 0 ? "awaiting decision" : "none"}
        />
        <Stat
          label="Annual leave"
          value={(() => {
            const a = balances.data?.balances.find((b) => b.leaveType.code === "ANNUAL")
            if (!a) return "—"
            const remaining = Number(a.entitledDays) - Number(a.usedDays)
            return `${remaining.toFixed(1)} d`
          })()}
          sub="remaining"
        />
      </div>

      {/* Recent payslips */}
      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center">
          <div className="font-medium">Recent payslips</div>
          <Link to={`/t/${slug}/me/payslips`} className="ml-auto text-xs text-brand-600 hover:text-brand-800">
            View all →
          </Link>
        </div>
        {recent.length === 0 ? (
          <div className="p-6 text-sm text-slate-400 text-center">No payslips yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-500 uppercase tracking-wide border-b border-slate-200">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Period</th>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-right px-4 py-2 font-medium">Net</th>
              </tr>
            </thead>
            <tbody>
              {recent.map((p) => (
                <tr key={p.id} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs">
                    {p.run.periodStart.slice(0, 10)} → {p.run.periodEnd.slice(0, 10)}
                  </td>
                  <td className="px-4 py-2 text-xs">{p.run.status}</td>
                  <td className="px-4 py-2 text-right font-mono font-semibold">{fmtMoney(p.netPay)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Leave balances */}
      <div className="bg-white border border-slate-200 rounded">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center">
          <div className="font-medium">Leave balances ({new Date().getFullYear()})</div>
          <Link to={`/t/${slug}/me/leave`} className="ml-auto text-xs text-brand-600 hover:text-brand-800">
            Submit / view →
          </Link>
        </div>
        {(balances.data?.balances ?? []).length === 0 ? (
          <div className="p-6 text-sm text-slate-400 text-center">No balances yet.</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 p-4">
            {balances.data!.balances.map((b) => {
              const remaining = Number(b.entitledDays) - Number(b.usedDays)
              return (
                <div key={b.leaveType.code} className="bg-slate-50 border border-slate-200 rounded p-2">
                  <div className="text-[10px] uppercase tracking-wide text-slate-500">{b.leaveType.code}</div>
                  <div className="text-base font-mono font-semibold">{remaining.toFixed(1)}</div>
                  <div className="text-[11px] text-slate-500">
                    {Number(b.usedDays).toFixed(1)} used
                    {Number(b.pendingDays) > 0 && <> · {Number(b.pendingDays).toFixed(1)} pending</>}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

function Stat(props: { label: string; value: string; sub?: string; accent?: boolean }) {
  return (
    <div className="bg-white border border-slate-200 rounded p-3">
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{props.label}</div>
      <div className={`text-xl font-mono ${props.accent ? "font-semibold text-emerald-700" : "text-slate-900"}`}>
        {props.value}
      </div>
      {props.sub && <div className="text-[11px] text-slate-500 mt-0.5">{props.sub}</div>}
    </div>
  )
}
